import { Location, LocationDescriptorObject } from 'history';
import { CourseId } from '../types/courses';
import { SectionId } from '../types/sections';
import * as queryString from 'querystring';
import { EventId } from '../../commonv3/types/events';
import { LessonId } from '../types/lessons';
import { SessionId } from '../../commonv3/types/sessions';
import { SessionTemplateKey, SessionType } from '../types/sessions';
import { CoachPositionType } from '../types/coachSkillCertifications';
import {
  ExamPracticalSectionId,
  ExamPracticalSectionSubmissionToken,
  ExamWritingSectionId,
} from '../../commonv3/types/courseExams';
import { TrialLessonId } from '../../commonv3/types/trialLessons';
import { MembershipItemId } from '../../commonv3/types/membershipPlans';
import { ApplicationPageKey } from '../types/applicationPages';
import { EventReservationId } from '../../commonv3/types/eventReservations';
import { ByServiceSurveyKeys, getSurveyKeyForService, SurveyKey, SurveyKeysPlane } from '../../commonv3/types/surveys';
import { LessonHomeworkId } from '../../commonv3/types/lesson_homeworks';
import { LessonHomeworkSubmissionToken } from '../../commonv3/types/lesson_homework_submissions';
import { UserTutorialCompletionKey } from '../types/userTutorialCompletions';
import { SheEnv } from '../../commonv3/utils/envs';
import { NotificationDeliveryToken } from '../types/notification_deliveries';
import { Service } from '../../commonv3/types/service';
import { ReservationId } from '../../adminv3/types/session';
import { MembershipPlanGroupKey } from '../types/membershipPlans';

export type EnhancedLocationDescriptor = LocationDescriptorObject & {
  stringify(): string;
  toAbsolute(): string;
};

function v3Path(path: string, params: Partial<LocationDescriptorObject> = {}): EnhancedLocationDescriptor {
  return {
    pathname: `/${path}`,
    stringify() {
      const search = this.search ? `?${this.search}` : '';
      const hash = this.hash ? this.hash : '';
      return `/${path}${hash}${search}`;
    },
    toAbsolute() {
      return `${location.origin}${this.stringify()}`;
    },
    ...params,
  };
}

const DynamicPath = {
  openEventOverlay(current: Location, eventId: number): LocationDescriptorObject {
    const { search } = current;
    const query = search.startsWith('?') ? search.slice(1) : search;
    return {
      ...current,
      search: queryString.stringify({ ...queryString.parse(query), event_id: eventId.toString() }),
    };
  },
  closeEventOverlay(current: Location): LocationDescriptorObject {
    const { search } = current;
    const query = queryString.parse(search.startsWith('?') ? search.slice(1) : search);
    delete query.event_id;
    return {
      ...current,
      search: queryString.stringify(query),
    };
  },
};

export const V3Routes = {
  DynamicPath,
  trial(anchor?: string) {
    return {
      pathname: '/trial',
      hash: anchor ? `#${anchor}` : undefined,
    };
  },
  trialDocument() {
    return {
      pathname: `/trial/document_01`,
    };
  },
  trialLessons() {
    return {
      pathname: `/trial_lessons`,
    };
  },
  trialLessonsMultiCreator() {
    return {
      pathname: `/trial_lessons/multicreator`,
    };
  },
  trialLessonReservationComplete(lessonId: TrialLessonId) {
    return {
      pathname: `/trial_lessons/${lessonId}/reservation_complete`,
    };
  },
  MultiCreatorTrialLessonReservationComplete(lessonId: TrialLessonId) {
    return {
      pathname: `/trial_lessons/multicreator/${lessonId}/reservation_complete`,
    };
  },
  trialLessonReschedule(reservationId: EventReservationId) {
    return {
      pathname: `/trial_reservations/${reservationId}/reschedule`,
    };
  },
  trialLessonRescheduleComplete(lessonId: TrialLessonId) {
    return {
      pathname: `/trial_lessons/${lessonId}/reschedule_complete`,
    };
  },
  trialFaq() {
    return '/trial/faq';
  },
  pricing() {
    return '/pricing';
  },
  trialReservation(id: TrialLessonId) {
    return {
      pathname: `/trial_lessons/${id}/reservation`,
    };
  },
  application() {
    return v3Path('application');
  },
  applicationPageSummary(page_key: ApplicationPageKey) {
    return v3Path(`application_pages/${page_key}/apply`);
  },
  applicationPageUserInfo(page_key: ApplicationPageKey) {
    return v3Path(`application_pages/${page_key}/user_info`);
  },
  applicationPageSignIn(page_key: ApplicationPageKey) {
    return v3Path(`application_pages/${page_key}/sign_in`);
  },
  applicationPageUserSummary(page_key: ApplicationPageKey) {
    return v3Path(`application_pages/${page_key}/user_summary`);
  },
  applicationPageHomeLocationSelect(page_key: ApplicationPageKey) {
    return v3Path(`application_pages/${page_key}/home_location`);
  },
  applicationPageStartDateSelect(page_key: ApplicationPageKey) {
    return v3Path(`application_pages/${page_key}/start_date`);
  },
  applicationPagePaymentMethod(page_key: ApplicationPageKey) {
    return v3Path(`application_pages/${page_key}/payment_method`);
  },
  applicationPageConfirmation(page_key: ApplicationPageKey) {
    return v3Path(`application_pages/${page_key}/summary`);
  },
  applicationPageComplete(page_key: ApplicationPageKey) {
    return v3Path(`application_pages/${page_key}/complete`);
  },
  multicreatorApplication() {
    return v3Path('apply/multicreator');
  },
  multicreatorApplicationSummary() {
    return v3Path(`apply/multicreator/apply`);
  },
  multicreatorApplicationUserInfo() {
    return v3Path(`apply/multicreator/user_info`);
  },
  multicreatorApplicationSignIn() {
    return v3Path(`apply/multicreator/sign_in`);
  },
  multicreatorApplicationUserSummary() {
    return v3Path(`apply/multicreator/user_summary`);
  },
  multicreatorApplicationHomeLocationSelect() {
    return v3Path(`apply/multicreator/home_location`);
  },
  multicreatorApplicationStartDateSelect() {
    return v3Path(`apply/multicreator/start_date`);
  },
  multicreatorApplicationPackageSelect() {
    return v3Path(`apply/multicreator/package`);
  },
  multicreatorApplicationPaymentMethod() {
    return v3Path(`apply/multicreator/payment_method`);
  },
  multicreatorApplicationConfirmation() {
    return v3Path(`apply/multicreator/summary`);
  },
  multicreatorApplicationComplete() {
    return v3Path(`apply/multicreator/complete`);
  },
  multicreatorFull() {
    return v3Path(`full/multicreators`);
  },
  beautyApplication() {
    return v3Path(`apply/beauty`);
  },
  moneyApplication() {
    return v3Path(`apply/money`);
  },
  applicationSignIn() {
    return v3Path('application/sign_in');
  },
  applicationUserInfo() {
    return v3Path('application/user_info');
  },
  applicationStartDateSelect() {
    return v3Path('application/start_date');
  },
  applicationHomeLocationSelect() {
    return v3Path('application/home_location');
  },
  applicationPlanSelect() {
    return v3Path('application/plans');
  },
  applicationConfirmation() {
    return v3Path('application/summary');
  },
  applicationPaymentMethod() {
    return v3Path('application/payment_method');
  },
  applicationAddressInfo() {
    return v3Path('application/user_address');
  },
  applicationComplete() {
    return v3Path('application/complete');
  },
  applicationError() {
    return v3Path('application/error');
  },
  examWritingSection(sectionId: ExamWritingSectionId) {
    return v3Path(`exam_writing_sections/${sectionId}`);
  },
  examPracticalSection(sectionId: ExamPracticalSectionId) {
    return v3Path(`exam_practical_sections/${sectionId}`);
  },
  examPracticalSectionSubmissionShow(token: LessonHomeworkSubmissionToken) {
    return v3Path(`exam_practical_section_submissions/${token}`);
  },
  examPracticalSectionSubmissionShowForReviewer(token: ExamPracticalSectionSubmissionToken) {
    return v3Path(`reviewer/exam_practical_section_submissions/${token}`);
  },
  examPracticalSectionSubmissionNew(sectionId: ExamPracticalSectionId) {
    return v3Path(`exam_practical_sections/${sectionId}/submissions/new`);
  },
  examPracticalSectionSubmissionComplete(sectionId: ExamPracticalSectionId) {
    return v3Path(`exam_practical_sections/${sectionId}/submissions/complete`);
  },
  examPracticalSectionSubmissionsIndex(sectionId: ExamPracticalSectionId) {
    return v3Path(`exam_practical_sections/${sectionId}/submissions`);
  },
  eventArchives() {
    return v3Path(`event_archives`);
  },
  home(redirect_url?: string, source?: string) {
    const redirectUrlParam = redirect_url ? { redirect_url } : '';
    const sourceParam = source ? { source } : '';
    const params = {
      ...redirectUrlParam,
      ...sourceParam,
    };
    return v3Path('home', { search: params ? queryString.stringify(params) : '' });
  },
  events(ref: string | null = null) {
    return v3Path('events', { search: ref ? queryString.stringify({ ref }) : '' });
  },
  coaching() {
    return v3Path('coaching');
  },
  // TODO: マイページリニューアルがリリースされたらお掃除する
  coachingSessions2(sourceType: string | null = null) {
    return v3Path('coaching/sessions', {
      search: sourceType ? queryString.stringify({ sourceType }) : '',
    });
  },
  coachingSessionReservation(sessionId: SessionId) {
    return v3Path(`coaching/sessions/${sessionId}/reservation`);
  },
  coachingSessionReservationComplete(sessionId: SessionId) {
    return v3Path(`coaching/sessions/${sessionId}/reservation_complete`);
  },
  careerCoachingSessions2() {
    return v3Path(`coaching/career_coaching_sessions`);
  },
  mokumoku() {
    return v3Path('mokumoku');
  },
  // TODO: マイページリニューアルがリリースされたらお掃除する
  mokumokuSessions2(key: MembershipPlanGroupKey = MembershipPlanGroupKey.LIKES, sourceType: string | null = null) {
    const param = sourceType
      ? {
          key: key,
          sourceType: sourceType,
        }
      : {
          key: key,
        };
    return v3Path('mokumoku/sessions', {
      search: queryString.stringify(param),
    });
  },
  mokumokuSessionReservation(sessionId: SessionId) {
    return v3Path(`mokumoku/sessions/${sessionId}/reservation`);
  },
  mokumokuSessionReservationComplete(sessionId: SessionId) {
    return v3Path(`mokumoku/sessions/${sessionId}/reservation_complete`);
  },
  // TODO: mokumoku配下に移動させる
  sessionReservationError(sessionId: SessionId, message: string) {
    return v3Path(`home/sessions/${sessionId}/reservation_error`, {
      search: message && queryString.stringify({ message }),
    });
  },
  mokumokuStudyRoom2() {
    return v3Path('mokumoku/mokumoku_study_room');
  },
  creators() {
    return v3Path('she_creators');
  },
  courses() {
    return v3Path('courses');
  },
  course(id: CourseId, preview = false) {
    return v3Path(`courses/${id}`, { search: preview ? queryString.stringify({ preview }) : '' });
  },
  courseSet(path: string) {
    return v3Path(`course/set/${path}`);
  },
  lesson(id: CourseId, lessonId: LessonId) {
    return v3Path(`courses/${id}/lessons/${lessonId}`);
  },
  courseSection(id: CourseId, lessonId: LessonId, sectionId: SectionId) {
    return v3Path(`courses/${id}/lessons/${lessonId}/sections/${sectionId}`);
  },
  section(id: SectionId) {
    return v3Path(`sections/${id}`);
  },
  myPage() {
    return v3Path('home/mypage');
  },
  homeLocation() {
    return v3Path('home/mypage/home_location');
  },
  homeLocationUpdate() {
    return v3Path('home_location/update');
  },
  plan() {
    return v3Path('home/mypage/plans');
  },
  multiCreators() {
    return v3Path('home/mypage/multicreators');
  },
  planUpdate(currentItemId: MembershipItemId) {
    return v3Path(`membership_plans/${currentItemId}/update`);
  },
  planUpdatePlanSelect(currentItemId: MembershipItemId) {
    return v3Path(`membership_plans/${currentItemId}/update/plans`);
  },
  planUpdatePaymentMethod(currentItemId: MembershipItemId) {
    return v3Path(`membership_plans/${currentItemId}/update/payment_method`);
  },
  planUpdateConfirm(currentItemId: MembershipItemId) {
    return v3Path(`membership_plans/${currentItemId}/update/confirm`);
  },
  planUpdateComplete(currentItemId: MembershipItemId) {
    return v3Path(`membership_plans/${currentItemId}/update/complete`);
  },
  payments() {
    return v3Path('home/mypage/payments');
  },
  payment_info() {
    return v3Path('home/mypage/payment_info/');
  },
  retry_payment(code: string) {
    return v3Path(`home/mypage/retry_payment/${code}`);
  },
  generate_receipt(code: string) {
    return v3Path(`home/mypage/payments/${code}/receipt`);
  },
  receipt(code: string, name: string, purpose: string, appelation: string) {
    return v3Path(
      `payments/${code}/receipt/?${queryString.stringify({
        name: name,
        purpose: purpose,
        appelation: appelation,
      })}`
    );
  },
  password() {
    return v3Path('home/mypage/password');
  },
  email() {
    return v3Path('home/mypage/email');
  },
  profile() {
    return v3Path('home/mypage/profile');
  },
  address() {
    return v3Path('home/mypage/address');
  },
  reservations() {
    return v3Path('home/mypage/reservations');
  },
  faq() {
    return v3Path('faq');
  },
  signOut() {
    return v3Path('sign_out');
  },
  signIn(redirect_url = undefined) {
    return v3Path('sign_in', {
      search: redirect_url ? queryString.stringify({ redirect_url }) : undefined,
    });
  },
  timesheetHome() {
    return v3Path('timesheet');
  },
  timesheetPositions(type?: CoachPositionType) {
    return v3Path('timesheet/positions', {
      search: type ? queryString.stringify({ type }) : undefined,
    });
  },
  timesheetComplete() {
    return v3Path('timesheet/complete');
  },
  timesheetError() {
    return v3Path('timesheet/error');
  },
  courseWorkSubmissions() {
    return v3Path('reviewer/course_work_submissions');
  },
  userCourseWorkSubmissions() {
    return v3Path('course_work_submissions');
  },
  communityService() {
    if (SheEnv.isProduction()) {
      return 'https://community.shelikes.jp';
    }
    if (SheEnv.isStaging()) {
      return 'https://staging.community.shelikes.jp';
    }
    return 'http://localhost:4000';
  },
  v2Migration() {
    return v3Path('migration');
  },
  goals() {
    return v3Path('home/goals');
  },
  idealCareer() {
    return v3Path('home/goals/ideal_career');
  },
  yearlyGoal() {
    return v3Path('home/goals/yearly');
  },
  monthlyGoal(year?: number, month?: number) {
    return v3Path('home/goals/monthly', {
      search: (year && month && queryString.stringify({ year, month })) || '',
    });
  },
  welcomeIntroduction() {
    return v3Path('home/welcome');
  },
  homeLocationIntroduction() {
    return v3Path('home/introduction/home_location');
  },
  homeLocationIntroductionComplete() {
    return v3Path('home/introduction/home_location/complete');
  },
  reserveCoaching() {
    return v3Path('home/introduction/coaching');
  },
  eventReservationAttendanceComplete(event_reservation_id: number) {
    return v3Path(`event_reservations/${event_reservation_id}/attendance/complete`);
  },
  reservationAttendanceComplete(reservation_id: number) {
    return v3Path(`reservations/${reservation_id}/attendance/complete`);
  },
  subscriptionSuspensionCancel(subscriptionCode: string, subscriptionSuspensionCode: string) {
    return v3Path(`subscription/${subscriptionCode}/suspension/${subscriptionSuspensionCode}/cancel`);
  },
  subscriptionSuspensionInfo(subscriptionCode: string) {
    return v3Path(`subscriptions/${subscriptionCode}/suspension`);
  },
  subscriptionSuspend(subscriptionCode: string) {
    return v3Path(`subscriptions/${subscriptionCode}/suspend`);
  },
  subscriptionSuspended(subscriptionCode: string) {
    return v3Path(`subscriptions/${subscriptionCode}/suspended`);
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  survey(key: SurveyKey | string, parameters: any = {}) {
    return v3Path(`surveys/${key}`, {
      search: queryString.stringify(parameters),
    });
  },
  trialLessonSurvey(id: TrialLessonId, service: Service = Service.LIKES) {
    return this.survey(getSurveyKeyForService(ByServiceSurveyKeys.TRIAL_LESSON, service), { trial_lesson_id: id });
  },
  eventSurvey(id: EventId, service: Service = Service.LIKES) {
    return this.survey(getSurveyKeyForService(ByServiceSurveyKeys.SPECIAL_EVENT, service), { event_id: id });
  },
  sessionSurvey(
    id: SessionId,
    sessionType: SessionType,
    service: Service = Service.LIKES,
    sessionTemplateKey: SessionTemplateKey
  ) {
    switch (sessionType) {
      case SessionType.GROUP_COACHING:
        if (
          sessionTemplateKey === SessionTemplateKey.ONLINE_INITIAL_GROUP_COACHING_V3_SECOND ||
          sessionTemplateKey === SessionTemplateKey.OFFLINE_INITIAL_GROUP_COACHING_V3_SECOND
        ) {
          // 2022年6月末まで運用の初回コーチング STEP2
          return this.survey(getSurveyKeyForService(SurveyKeysPlane.GROUP_COACHING_INITIAL), {
            session_id: id,
          });
        } else if (
          sessionTemplateKey === SessionTemplateKey.ONLINE_INITIAL_COACHING_WELCOME_WS ||
          sessionTemplateKey === SessionTemplateKey.OFFLINE_INITIAL_COACHING_WELCOME_WS
        ) {
          return this.survey(getSurveyKeyForService(SurveyKeysPlane.WELCOME_WORKSHOP), {
            session_id: id,
          });
        } else {
          return this.survey(getSurveyKeyForService(ByServiceSurveyKeys.GROUP_COACHING, service), { session_id: id });
        }
      case SessionType.MOKUMOKU:
        return this.survey(getSurveyKeyForService(ByServiceSurveyKeys.MOKUMOKU, service), { session_id: id });
    }

    throw new Error('Unsupported sessionType : ' + sessionType);
  },
  userTutorialDetail(page_key: UserTutorialCompletionKey) {
    return v3Path(`home/user_tutorials/${page_key}`);
  },
  withdrawalApply() {
    return v3Path('withdrawal');
  },
  withdrawalForm() {
    return v3Path('withdrawal/form');
  },
  withdrawalComplete() {
    return v3Path('withdrawal/complete');
  },
  withdrawalCancel() {
    return v3Path('withdrawal/cancel');
  },
  subscriptionSuspensionCancelInfo(code: string) {
    return v3Path(`subscriptions/${code}/suspension/cancel`);
  },
  forgotPassword() {
    return v3Path(`passwords/forgot`);
  },
  validateResetCode() {
    return v3Path(`passwords/reset_code`);
  },
  resetPassword() {
    return v3Path(`passwords/reset`);
  },
  resetPasswordComplete() {
    return v3Path(`passwords/reset/complete`);
  },
  lessonHomeworkSubmission(homeworkId: LessonHomeworkId) {
    return v3Path(`lesson_homeworks/${homeworkId}/submissions/new`);
  },
  lessonHomeworkSubmissionShow(token: LessonHomeworkSubmissionToken) {
    return v3Path(`lesson_homework_submissions/${token}`);
  },
  lessonHomeworkSubmissionShowForReviewer(token: LessonHomeworkSubmissionToken) {
    return v3Path(`reviewer/lesson_homework_submissions/${token}`);
  },
  lessonHomeworkSubmissionComplete(homeworkId: LessonHomeworkId) {
    return v3Path(`lesson_homeworks/${homeworkId}/submissions/complete`);
  },
  lessonHomeworkSubmissionsIndex(homeworkId: LessonHomeworkId) {
    return v3Path(`lesson_homeworks/${homeworkId}/submissions`);
  },
  NotificationDeliveryShowPage(token: NotificationDeliveryToken) {
    return v3Path(`notifications/${token}`);
  },
  NotificationDeliveryListPage() {
    return v3Path(`notifications`);
  },
  likesHome() {
    if (SheEnv.isProduction()) {
      return 'https://shelikes.jp';
    }
    if (SheEnv.isStaging()) {
      return 'https://staging.shelikes.jp';
    }
    return 'http://localhost:3000';
  },
  likesUrl(path: string) {
    if (SheEnv.isProduction()) {
      return `https://shelikes.jp${path}`;
    }
    if (SheEnv.isStaging()) {
      return `https://staging.shelikes.jp${path}`;
    }
    return `http://localhost:3000${path}`;
  },
};

function beautyPath(path: string, params: Partial<LocationDescriptorObject> = {}): EnhancedLocationDescriptor {
  let basePath = 'http://localhost:4001';
  if (SheEnv.isProduction()) {
    basePath = 'https://beauty.shelikes.jp';
  }
  if (SheEnv.isStaging()) {
    basePath = 'https://staging.beauty.shelikes.jp';
  }
  return {
    pathname: `${basePath}/${path}`,
    stringify() {
      const search = this.search ? `?${this.search}` : '';
      const hash = this.hash ? this.hash : '';
      return `/${path}${hash}${search}`;
    },
    toAbsolute() {
      return `${location.origin}${this.stringify()}`;
    },
    ...params,
  };
}

export const BeautyRoutes = {
  trialLessons() {
    return beautyPath(`trial_lessons`);
  },
  trialLessonReschedule(id: ReservationId) {
    return beautyPath(`trial_lessons/${id.toString()}/reschedule`);
  },
  home() {
    return beautyPath('');
  },
  memberHome() {
    return beautyPath('home');
  },
};

function moneyPath(path: string, params: Partial<LocationDescriptorObject> = {}): EnhancedLocationDescriptor {
  let basePath = 'http://localhost:5001';
  if (SheEnv.isProduction()) {
    basePath = 'https://money.shelikes.jp';
  }
  if (SheEnv.isStaging()) {
    basePath = 'https://staging.money.shelikes.jp';
  }
  return {
    pathname: `${basePath}/${path}`,
    stringify() {
      const search = this.search ? `?${this.search}` : '';
      const hash = this.hash ? this.hash : '';
      return `/${path}${hash}${search}`;
    },
    toAbsolute() {
      return `${location.origin}${this.stringify()}`;
    },
    ...params,
  };
}

export const MoneyRoutes = {
  trialLessons() {
    return moneyPath(`trial_lessons`);
  },
  home() {
    return moneyPath('');
  },
  memberHome() {
    return moneyPath('home');
  },
};

function accountPath(path: string, params: Partial<LocationDescriptorObject> = {}): EnhancedLocationDescriptor {
  let basePath = `${window.location.origin}/account`;
  if (SheEnv.isProduction()) {
    basePath = 'https://account.shelikes.jp';
  }
  if (SheEnv.isStaging()) {
    basePath = 'https://staging.account.shelikes.jp';
  }
  const pathname = `${basePath}/${path}`;
  return {
    pathname,
    stringify() {
      const search = this.search ? `?${this.search}` : '';
      const hash = this.hash ? this.hash : '';
      return `${pathname}${hash}${search}`;
    },
    toAbsolute() {
      return pathname;
    },
    ...params,
  };
}

/**
 * Likes側のコードからアカウントページに遷移する時に用いる
 */
export const AbsoluteAccountRoutes = {
  home() {
    return accountPath('');
  },
};

/**
 * SocialProfileのURL
 */
export const SocialProfileRoot = (): string => {
  let rootUrl: string;
  if (SheEnv.isProduction()) {
    rootUrl = 'https://profile.shelikes.jp/';
  } else if (SheEnv.isStaging()) {
    rootUrl = 'https://staging.profile.shelikes.jp/';
  } else {
    rootUrl = 'http://localhost:3001/';
  }

  return rootUrl;
};

/**
 * Commmune（SHE station）のURL
 */
export const CommmuneRoute = (path?: string) => {
  if (path !== undefined) {
    return `https://shestation.shelikes.jp/${path}`;
  }
  return 'https://shestation.shelikes.jp/';
};
export const COMMMUNE_SHE_CREATORS_SCHEDULE_PATH = 'view/box?boxId=1C0L2qrEs&categoryIndex=7';
export const COMMMUNE_CHECK_COMPANY_JOB_PATH = 'view/box?boxId=1C0L2qrEs&categoryIndex=4';
export const COMMMUNE_CHECK_SNS_COMPE_PATH = 'view/box?boxId=1C0L2qrEs&categoryIndex=1';
export const COMMMUNE_CHECK_WRITER_COMPE_PATH = 'view/box?boxId=1C0L2qrEs&categoryIndex=5';
export const COMMMUNE_MOKUMOKU_STUDY_ROOM_PATH = 'view/box?boxId=smDLFiLLL&categoryIndex=7';

/**
 * Notion の URL
 */
export const NOTION_STUDY_PLANNING = 'https://sheinc.notion.site/Notion-ad410b6c2420471aa8a8076e0b8bca78';
export const NOTION_COACHING_GUIDE_URL = 'https://sheinc.notion.site/SHElikes-90a961bd21fc4199bad6008a82cc30a5';
export const NOTION_LIST_OF_COACH_URL =
  'https://sheinc.notion.site/7328ddf155cb4b529f274172f64b5ed1?v=daa50fe143894820a8d53d0b24019d00';
export const NOTION_1_ON_1_COACHING_URL = 'https://sheinc.notion.site/1on1-4d73b46d57154f29940dab13825dff58';
export const NOTION_MOKUMOKU_GUIDE_URL = 'https://sheinc.notion.site/SHElikes-f50c771a10da4d49899f78f801b4fad7';
export const NOTION_OTHER_MOKUMOKU_TYPE_URL =
  'https://sheinc.notion.site/SHElikes-f50c771a10da4d49899f78f801b4fad7#a101b0ebe11d43ff8c15567b8920a083';
export const NOTION_SHE_CREATORS_GUIDE_URL = 'https://sheinc.notion.site/f88d446247bf49cf9ea5b2b424d2ea38';
export const NOTION_CHECK_TYPE_OF_COMPANY_JOB_URL = 'https://sheinc.notion.site/8011a95f45b344f1ac129812f3b4cb97';
export const NOTION_CHECK_TYPE_OF_SNS_JOB_URL = 'https://sheinc.notion.site/SHE-757eeedb2e034a84b104a9b5047567d8';

/**
 * Google Slide の URL
 */
export const GOOGLE_SLIDE_LIST_OF_TA_URL =
  'https://docs.google.com/presentation/d/1gz7GnKQP_kK3KJZoyyYCF-9xc0Ut4qDe87dk_25FS9Y/edit#slide=id.gb8e575184f_0_201';
export const GOOGLE_SLIDE_HOW_TO_USE_MOKUMOKU_URL =
  'https://docs.google.com/presentation/d/1H9P8rmczlKWVdNSmsSKfAuSWxvVB18LXCNKoR-b_3uE/';
export const GOOGLE_SLIDE_TYPE_OF_MOKUMOKU_URL =
  'https://docs.google.com/presentation/d/1jRiJoytmQRFhCF3iFTyKTLq9xEbnd3fhxmP9Cq1Iucc/edit#slide=id.g1344a40deb8_0_404';
